import { useCallback, useMemo } from 'react'

import { mapDraftContentToPrefillMedia } from '~publish/components/ContentItemCard'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import { isOfType } from '~publish/helpers/typeGuards'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import type { PrefillPostData } from '~publish/hooks/usePostComposer'

const EDIT_CTA = 'publish-home-draftsCard-editContentItemDraft-1'

/**
 * Opens a channel-less draft in the composer's omnibox so Save updates it in
 * place, seeded with the draft's text, tags, media and — when a targetDate is
 * set — the schedule selector (as dueAt, unix seconds). Home-local equivalent
 * of the drafts tab's `useContentItemDraftActions().edit`, kept separate so
 * the Home card doesn't couple to the drafts tab's action set and CTA.
 */
export function useEditContentItemDraft(
  contentItem: ContentItemDraft,
): () => void {
  const { editContentItemDraftInComposer } = usePostComposer()

  const draftBody = isOfType(contentItem.body, 'DraftContent')
    ? contentItem.body
    : null

  const prefillPostData = useMemo((): PrefillPostData => {
    if (!draftBody) return {}
    const prefill: PrefillPostData = {
      text: draftBody.text,
      tags: contentItem.tags.map((tag) => tag.id),
      ...mapDraftContentToPrefillMedia(draftBody.assets),
    }
    if (contentItem.targetDate) {
      prefill.dueAt = Math.floor(
        new Date(contentItem.targetDate).getTime() / 1000,
      )
    }
    return prefill
  }, [draftBody, contentItem.tags, contentItem.targetDate])

  return useCallback(() => {
    void editContentItemDraftInComposer({
      cta: EDIT_CTA,
      contentItemId: contentItem.id,
      contentItemTitle: contentItem.title,
      prefillPostData,
    })
  }, [
    editContentItemDraftInComposer,
    contentItem.id,
    contentItem.title,
    prefillPostData,
  ])
}
